// Feature flags the UI reads once from GET /features (jobq-api get_features).
//
// pallet_beacons = {enabled, location}: shows the beacon card on the TRK popup, the Beacons page and
// the Add Sheet docked-pallet fill-in (jobq-api pallet_beacons.py; dev only at first). `location`
// (jobq-api BEACON_LOCATION_ENABLED_ENVS, 2026-10-20) adds the shop map: the map under every Find,
// the /shop-map page and its button. Never on unless beacons are on too.
//
// mobile = {enabled, pages}: the phone app switch (jobq-api mobile_app.py). A phone-width screen
// gets the phone layout only for the listed pages (src/mobile/mobileMode.js); pages is [] whenever
// enabled is not true, so a half-filled answer never switches anything on.
//
// tm_pricing = {enabled, env, pilot_customer_ids}. It gates only the
// CREATION of Time & Materials quotes (the quote page's Pricing toggle). Every T&M lock, banner
// and refusal in the UI keys on the RECORD (pricing_mode / so_pricing_mode / tm_* keys) instead,
// so switching the flag off never hides a lock on an order that is already T&M.
//
// Rules:
//   * one request per page load (module-cached promise); a FAILED request is not cached, so the
//     next caller asks again;
//   * any failure, an old API without the route, or an odd body (the smoke tests' fetch fallback
//     answers `[]`) reads as "disabled" — never as enabled.

import { useEffect, useState } from 'react';
import config from '../config.js';

export const FEATURES_DISABLED = Object.freeze({
  loaded: false,
  tmPricing: Object.freeze({ enabled: false, env: null, pilotCustomerIds: null }),
  palletBeacons: Object.freeze({ enabled: false, location: false, soPallets: false }),
  mobile: Object.freeze({ enabled: false, pages: Object.freeze([]) }),
});

/** GET /features "mobile" -> {enabled, pages}: page keys are non-empty strings, de-duplicated. */
export function parseMobile(mb) {
  const ok = mb && typeof mb === 'object' && !Array.isArray(mb) && mb.enabled === true;
  if (!ok) return { enabled: false, pages: [] };
  const pages = Array.isArray(mb.pages)
    ? [...new Set(mb.pages.filter((k) => typeof k === 'string' && k.trim()).map((k) => k.trim()))]
    : [];
  return { enabled: true, pages };
}

/** GET /features "pallet_beacons" -> {enabled, location, soPallets}; location and soPallets (beacons on
 *  sales-order pallets, jobq-api `so_pallet_beacons_mode`, 2026-10-22) only ride on enabled. */
export function parsePalletBeacons(pb) {
  const ok = !!(pb && typeof pb === 'object' && !Array.isArray(pb) && pb.enabled === true);
  const loc = ok ? pb.location : null;
  const location = loc === true || !!(loc && typeof loc === 'object' && !Array.isArray(loc) && loc.enabled === true);
  return { enabled: ok, location: ok && location, soPallets: ok && pb.so_pallets === true };
}

/** Normalise a GET /features body. Tolerates null, [], a string, missing keys. */
export function parseFeatures(body) {
  const obj = body && typeof body === 'object' && !Array.isArray(body) ? body : null;
  const tm = obj ? obj.tm_pricing : null;
  const pb = obj ? obj.pallet_beacons : null;
  const ok = tm && typeof tm === 'object' && !Array.isArray(tm);
  const pilot = ok && Array.isArray(tm.pilot_customer_ids)
    ? tm.pilot_customer_ids.map((c) => Number(c)).filter((c) => Number.isInteger(c))
    : null;
  return {
    loaded: true,
    tmPricing: {
      enabled: !!(ok && tm.enabled === true),
      env: ok && typeof tm.env === 'string' ? tm.env : null,
      pilotCustomerIds: pilot,
    },
    palletBeacons: parsePalletBeacons(pb),
    mobile: parseMobile(obj ? obj.mobile : null),
  };
}

let cache = null; // { token, promise }

async function loadFeatures() {
  try {
    const res = await fetch(`${config.API_BASE_URL}/features`);
    if (!res || !res.ok) return { ok: false, features: { ...FEATURES_DISABLED, loaded: true } };
    const body = await res.json();
    return { ok: true, features: parseFeatures(body) };
  } catch (e) {
    return { ok: false, features: { ...FEATURES_DISABLED, loaded: true } };
  }
}

/** The features, fetched once per page load. A failed fetch resolves "disabled" and is NOT cached. */
export function fetchFeatures() {
  if (!cache) {
    const token = {};
    cache = {
      token,
      promise: loadFeatures().then((r) => {
        if (!r.ok && cache && cache.token === token) cache = null;
        return r.features;
      }),
    };
  }
  return cache.promise;
}

/** React hook: FEATURES_DISABLED until the fetch lands, then the parsed features. */
export function useFeatures() {
  const [features, setFeatures] = useState(FEATURES_DISABLED);
  useEffect(() => {
    let alive = true;
    fetchFeatures().then((f) => { if (alive) setFeatures(f); });
    return () => { alive = false; };
  }, []);
  return features;
}

/**
 * The server's customer rule for a T&M quote (tm_pricing.tm_customer_allowed): customer 1 is
 * never T&M; with a pilot list, only those customers. Says nothing about the flag itself —
 * callers check features.tmPricing.enabled as well.
 */
export function tmCustomerAllowed(features, customerId) {
  const cid = Number(customerId);
  if (customerId === null || customerId === undefined || customerId === '' || !Number.isInteger(cid)) return false;
  if (cid === 1 || cid <= 0) return false;
  const pilot = features && features.tmPricing ? features.tmPricing.pilotCustomerIds : null;
  if (Array.isArray(pilot)) return pilot.includes(cid);
  return true;
}

/** Whether a NEW T&M quote may be started for this customer (flag on + customer allowed). */
export function tmCreationAllowed(features, customerId) {
  return !!(features && features.tmPricing && features.tmPricing.enabled) && tmCustomerAllowed(features, customerId);
}

export function __resetFeaturesForTests() {
  cache = null;
}
